"use client"
// React and form imports
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// UI components
import { Button } from "@external/landing/components/ui/button";
import { Input } from "@external/landing/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@external/landing/components/ui/card";
import { motion } from "framer-motion";

// Utilities
import "@/components/i18n/i18n";
import { useTranslation } from "@/components/i18n/next";
import { useLocation, useNavigate } from "@/components/router-wrapper";
import { fetchWithAuth } from "@external/landing/lib/utils";
import {
  clearStoredAttribution,
  getStoredAttribution,
} from "@/lib/marketing-attribution";

// Form schemas
const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  refferal: z.string().optional(),
  password: z.string().min(6, "Password must be at least 6 characters")
});

const registerSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  refferal: z.string().optional(),
  password: z.string().min(6, "Password must be at least 6 characters"),
  passwordConfirmation: z.string().min(6, "Password must be at least 6 characters"),
  acceptTerms: z.boolean().refine(val => val === true, {
    message: "You must accept the terms and conditions"
  }),
  acceptCommunication: z.boolean()
}).refine(data => data.password === data.passwordConfirmation, {
  message: "Passwords do not match",
  path: ["passwordConfirmation"]
});

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address")
});

type FormType = "login" | "register" | "forgot";

type LoginFormData = z.infer<typeof loginSchema>;
type RegisterFormData = z.infer<typeof registerSchema>;
type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

interface AuthFormProps {
  initialFormType?: FormType;
}

const AuthForm = ({ initialFormType = "login" }: AuthFormProps) => {
  const [formType, setFormType] = useState<FormType>(initialFormType);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [forgotPasswordError, setForgotPasswordError] = useState<string | null>(null);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<string | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Set formType when initialFormType changes
  useEffect(() => {
    setFormType(initialFormType);
  }, [initialFormType]);
  
  // Login form
  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    }
  });

  // Register form
  const registerForm = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      acceptTerms: false,
      acceptCommunication: false
    }
  });

  // Forgot password form
  const forgotPasswordForm = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: ""
    }
  });

  const onLoginSubmit = async (data: LoginFormData) => {
    if (data.email) {
      data.email = data.email.toLowerCase();
    }

    const refferal = localStorage.getItem("refferal");
    if (refferal) {
      data.refferal = refferal as string;
    }
    try {
      setLoginError(null);
      const response = await fetchWithAuth("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include", // This enables cookies to be set by the server
        body: JSON.stringify(data)
      });

      const responseData = await response.json();

      console.log(responseData);
      
      if (!response.ok) {
        const errorMessage = responseData.error?.message || "Login failed";
        setLoginError(errorMessage);
        throw new Error(errorMessage);
      }

      // Store user data in localStorage
      if (responseData.data?.user) {
        localStorage.setItem('user', JSON.stringify(responseData.data.user));
      }

      // Handle successful login
      window.location.href = "/";
    } catch (error) {
      console.error("Login error:", error);
      // Error is now handled via the loginError state
    }
  };

  const onRegisterSubmit = async (data: RegisterFormData) => {
    if (data.email) {
      data.email = data.email.toLowerCase();
    }
    const refferal = localStorage.getItem("refferal");
    if (refferal) {
      data.refferal = refferal as string;
    }
    const attribution = getStoredAttribution();
    const registerBody =
      attribution != null && attribution.length > 0
        ? { ...data, attribution }
        : { ...data };
    try {
      setRegisterError(null);
      const response = await fetchWithAuth("/api/auth/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(registerBody)
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        const errorMessage = responseData.error?.message || "Registration failed";
        setRegisterError(errorMessage);
        throw new Error(errorMessage);
      }

      clearStoredAttribution();

      // Handle successful registration
      navigate("/auth/login");
    } catch (error) {
      console.error("Registration error:", error);
      // Error is now handled via the registerError state
    }
  };

  const onForgotPasswordSubmit = async (data: ForgotPasswordFormData) => {
    if (data.email) {
      data.email = data.email.toLowerCase();
    }
    try {
      setForgotPasswordError(null);
      setForgotPasswordSuccess(null);
      const response = await fetchWithAuth("/api/auth/request-reset-password", {
        headers: {
          "Content-Type": "application/json"
        },
        method: "POST",
        body: JSON.stringify({ email: data.email })
      });

      const responseData = await response.json();
      
      if (!response.ok) {
        const errorMessage = responseData.error?.message || "Password reset request failed";
        setForgotPasswordError(errorMessage);
        throw new Error(errorMessage);
      }

      // Handle successful password reset request
      setForgotPasswordSuccess("Password reset instructions have been sent to your email");
    } catch (error) {
      console.error("Password reset error:", error);
      // Error is now handled via the forgotPasswordError state
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full max-w-md mx-auto"
    >
      <Card className="border border-border bg-card/30 backdrop-blur-md">
        <CardHeader>
          <CardTitle className="text-2xl font-bold text-center">
            {formType === "login" && t("Login")}
            {formType === "register" && t("Create an account")}
            {formType === "forgot" && t("Forgot Password")}
          </CardTitle>
          <CardDescription className="text-center">
            {formType === "login" && t("Enter your credentials to access your account")}
            {formType === "register" && t("Fill in your details to create a new account")}
            {formType === "forgot" && t("Enter your email to reset your password")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {formType === "login" && (
            <form onSubmit={loginForm.handleSubmit(onLoginSubmit)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  {t("Email")}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  {...loginForm.register("email")}
                />
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-destructive">{loginForm.formState.errors.email.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  {t("Password")}
                </label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  {...loginForm.register("password")}
                />
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-destructive">{loginForm.formState.errors.password.message}</p>
                )}
              </div>
              
              {loginError && (
                <div className="p-3 bg-destructive/10 border border-destructive rounded text-destructive text-sm">
                  {loginError}
                </div>
              )}
              
              <Button type="submit" className="w-full text-black">
                {t("Login")}
              </Button>
            </form>
          )}
          
          {formType === "register" && (
            <form onSubmit={registerForm.handleSubmit(onRegisterSubmit)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">
                  {t("Name")}
                </label>
                <Input
                  id="name"
                  placeholder="John Doe"
                  {...registerForm.register("name")}
                />
                {registerForm.formState.errors.name && (
                  <p className="text-xs text-destructive">{registerForm.formState.errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  {t("Email")}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  {...registerForm.register("email")}
                />
                {registerForm.formState.errors.email && (
                  <p className="text-xs text-destructive">{registerForm.formState.errors.email.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  {t("Password")}
                </label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  {...registerForm.register("password")}
                />
                {registerForm.formState.errors.password && (
                  <p className="text-xs text-destructive">{registerForm.formState.errors.password.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <label htmlFor="passwordConfirmation" className="text-sm font-medium">
                  {t("Confirm Password")}
                </label>
                <Input
                  id="passwordConfirmation"
                  type="password"
                  placeholder="••••••••"
                  {...registerForm.register("passwordConfirmation")}
                />
                {registerForm.formState.errors.passwordConfirmation && (
                  <p className="text-xs text-destructive">{registerForm.formState.errors.passwordConfirmation.message}</p>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="acceptTerms"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  {...registerForm.register("acceptTerms")}
                />
                <label htmlFor="acceptTerms" className="text-xs">
                  {t("I accept the")} <a href="/terms" className="text-primary hover:underline">{t("Terms and Conditions")}</a>
                </label>
              </div>
              {registerForm.formState.errors.acceptTerms && (
                <p className="text-xs text-destructive">{registerForm.formState.errors.acceptTerms.message}</p>
              )}
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="acceptCommunication"
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  {...registerForm.register("acceptCommunication")}
                />
                <label htmlFor="acceptCommunication" className="text-xs">
                  {t("I agree to receive communications from BetUp.AI")}
                </label>
              </div>
              
              {registerError && (
                <div className="p-3 bg-destructive/10 border border-destructive rounded text-destructive text-sm">
                  {registerError}
                </div>
              )}
              
              <Button type="submit" className="w-full text-black">
                {t("Register")}
              </Button>
            </form>
          )}
          
          {formType === "forgot" && (
            <form onSubmit={forgotPasswordForm.handleSubmit(onForgotPasswordSubmit)} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  {t("Email")}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@example.com"
                  {...forgotPasswordForm.register("email")}
                />
                {forgotPasswordForm.formState.errors.email && (
                  <p className="text-xs text-destructive">{forgotPasswordForm.formState.errors.email.message}</p>
                )}
              </div>
              
              {forgotPasswordError && (
                <div className="p-3 bg-destructive/10 border border-destructive rounded text-destructive text-sm">
                  {forgotPasswordError}
                </div>
              )}
              
              {forgotPasswordSuccess && (
                <div className="p-3 bg-green-100 border border-green-500 rounded text-green-700 text-sm">
                  {forgotPasswordSuccess}
                </div>
              )}
              
              <Button type="submit" className="w-full text-black">
                {t("Reset Password")}
              </Button>
            </form>
          )}
        </CardContent>
        <CardFooter className="flex flex-col space-y-4">
          {formType === "login" && (
            <>
              <Button
                variant="link"
                className="text-xs text-primary"
                onClick={() => navigate("/auth/forgot-password")}
              >
                {t("Forgot your password?")}
              </Button>
              <div className="text-center text-sm">
                {t("Don't have an account?")}
                <Button
                  variant="link"
                  className="text-primary pl-1"
                  onClick={() => navigate("/auth/register")}
                >
                  {t("Register")}
                </Button>
              </div>
            </>
          )}
          
          {formType === "register" && (
            <div className="text-center text-sm">
              {t("Already have an account?")}
              <Button
                variant="link"
                className="text-primary pl-1"
                onClick={() => navigate("/auth/login")}
              >
                {t("Login")}
              </Button>
            </div>
          )}
          
          {formType === "forgot" && (
            <Button
              variant="link"
              className="text-primary mx-auto"
              onClick={() => navigate("/auth/login")}
            >
              {t("Back to login")}
            </Button>
          )}
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default AuthForm;

// Route-specific components for better integration with the router
export const LoginForm = () => <AuthForm initialFormType="login" />;
export const RegisterForm = () => <AuthForm initialFormType="register" />;
export const ForgotPasswordForm = () => <AuthForm initialFormType="forgot" />; 